import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject, firstValueFrom, of } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';
import { PermissaoService } from './permissao/permissao.service';
import { CarrinhoService } from './carrinho.service';
import { EmpresaConfigService } from './configuracoes/empresa.service';
import { CotacaoService } from './cotacao/cotacao.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private isAuthenticatedSubject: BehaviorSubject<boolean>;
  isAuthenticated$ = new BehaviorSubject<boolean>(false).asObservable(); // sera trocado no ctor

  // Emite quando a verificacao inicial terminar
  private initializedSubject = new BehaviorSubject<boolean>(false);
  initialized$ = this.initializedSubject.asObservable();

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private permissaoService: PermissaoService,
    private carrinhoService: CarrinhoService,
    private empresaConfigService: EmpresaConfigService,
    private cotacaoService: CotacaoService,
  ) {
    const initial = this.hasValidSession();
    this.isAuthenticatedSubject = new BehaviorSubject<boolean>(initial);
    this.isAuthenticated$ = this.isAuthenticatedSubject.asObservable();
  }

  hasValidSession(): boolean {
    if (!isPlatformBrowser(this.platformId)) return false;
    const token = sessionStorage.getItem('authToken') ?? localStorage.getItem('authToken');
    const uid   = sessionStorage.getItem('id')        ?? localStorage.getItem('id');
    return !!(token || uid);
  }

  setAuthenticated(v: boolean) { this.isAuthenticatedSubject.next(v); }

  clearSessionAndBroadcast() {
    // Limpar permissoes antes de limpar sessao
    this.permissaoService.limparPermissoes();
    // Limpar carrinho e caches de vendas
    this.carrinhoService.clearAll();
    // Limpar cache da sessao da empresa (evita vazar dados entre usuarios)
    this.empresaConfigService.clearSessaoCache();
    // Limpar cache da ultima cotacao (evita vazar dados entre usuarios)
    this.cotacaoService.clearCotacaoCache();
    if (isPlatformBrowser(this.platformId)) {
      ['authToken','id','userId','email','categoriaAdmin','categoriaAcessoMaster'].forEach(k => {
        sessionStorage.removeItem(k);
        localStorage.removeItem(k);
      });
    }
    this.setAuthenticated(false);
  }

  /** Chamado pelo APP_INITIALIZER ANTES do app renderizar */
  bootstrapAuth(): Promise<void> {
    return new Promise((resolve) => {
      const ok = this.hasValidSession();
      this.isAuthenticatedSubject.next(ok);

      if (ok && isPlatformBrowser(this.platformId)) {
        // Obter email do storage para carregar permissoes
        const email = sessionStorage.getItem('email') || localStorage.getItem('email');
        if (email) {
          firstValueFrom(this.permissaoService.carregarPermissoes(email)).then(() => {
            this.initializedSubject.next(true);
            resolve();
          });
          return;
        }
        // Sessao valida mas sem email: carregar com permissao total para nao bloquear guards
        this.permissaoService.carregarComPermissaoTotal();
        this.initializedSubject.next(true);
        resolve();
        return;
      }

      // Sem sessao valida: marcar como inicializado
      this.initializedSubject.next(true);
      resolve();
    });
  }
}
